import type { MatchDetailResponse } from '../../../../../api/useSupplierReconciliation/useMatchDetail'
import { useApiClient } from '../../../../../hooks/useApiClient'
import type { components } from '../../../../../types/api/v1'

type BolCandidate = components['schemas']['BolCandidate']
type InvoiceCandidate = components['schemas']['InvoiceCandidate']

interface ManageMatchDualParams {
  invoice_document_id?: string
  bol_document_id?: string
}

interface ManageMatchDualData {
  invoices: InvoiceCandidate[]
  bols: BolCandidate[]
}

/**
 * Hook for fetching both BOL and invoice options data simultaneously
 * This ensures we have both datasets available for cross-referencing regardless of active tab
 */
export function useManageMatchDataDual(params: ManageMatchDualParams) {
  const $api = useApiClient()

  // Fetch BOL options (returns BolCandidate[])
  const bolQuery = $api.useQuery(
    'post',
    '/supplier_rec/match/options_for_invoice',
    {
      params: {
        query: {
          invoice_id: params.invoice_document_id,
        },
      },
    },
    {
      enabled: !!params.invoice_document_id,
      retry: false,
      refetchOnWindowFocus: false,
    }
  )

  // Fetch invoice options (returns InvoiceCandidate[])
  const invoiceQuery = $api.useQuery(
    'post',
    '/supplier_rec/match/options_for_bol',
    {
      params: {
        query: {
          bol_id: params.bol_document_id,
        },
      },
    },
    {
      enabled: !!params.bol_document_id,
      retry: false,
      refetchOnWindowFocus: false,
    }
  )

  // Combine data from both queries
  const combinedData: ManageMatchDualData = {
    bols: (bolQuery.data as BolCandidate[]) || [],
    invoices: (invoiceQuery.data as InvoiceCandidate[]) || [],
  }

  // Combined loading/error states
  const isLoading = bolQuery.isLoading || invoiceQuery.isLoading
  const error = bolQuery.error || invoiceQuery.error
  const isSuccess = bolQuery.isSuccess && invoiceQuery.isSuccess
  const isFetching = bolQuery.isFetching || invoiceQuery.isFetching

  // Refetch both queries
  const refetch = async () => {
    const results = await Promise.allSettled([bolQuery.refetch(), invoiceQuery.refetch()])
    return results
  }

  /**
   * Get data for specific search type (maintains backward compatibility)
   */
  function getDataForSearchType(searchType: 'invoice' | 'bol'): BolCandidate[] | InvoiceCandidate[] {
    if (searchType === 'bol') {
      return combinedData.bols
    } else {
      return combinedData.invoices
    }
  }

  /**
   * Check if an item has linked data indicating it's part of an existing match
   * @param item - item to check for linked data
   * @returns object with hasLinkedData flag and potential match_id
   */
  function checkForLinkedData(item: BolCandidate | InvoiceCandidate): {
    hasLinkedData: boolean
    matchId?: string
  } {
    const isBol = 'bol_id' in item

    if (isBol) {
      const bol = item as BolCandidate
      const linkedInvoiceIds = bol.linked_invoice_ids || []
      const linkedInvoiceNumbers = bol.linked_invoice_numbers || []
      const hasLinkedData = linkedInvoiceIds.length > 0 || linkedInvoiceNumbers.length > 0

      return {
        hasLinkedData,
        matchId: bol.match_id,
      }
    } else {
      const invoice = item as InvoiceCandidate
      const linkedBolIds = invoice.linked_bol_ids || []
      const linkedBolNumbers = invoice.linked_bol_numbers || []
      const hasLinkedData = linkedBolIds.length > 0 || linkedBolNumbers.length > 0

      return {
        hasLinkedData,
        matchId: invoice.match_id,
      }
    }
  }

  /**
   * Hook for fetching complete match data using match_id
   * Returns a mutation that can be called with a matchId
   */
  function useMatchDetailMutation() {
    return $api.useMutation('post', '/supplier_rec/match/detail', {})
  }

  /**
   * Fetch complete match data using match_id
   * @param matchDetailMutation - The mutation hook result
   * @param matchId - ID of the match to fetch
   * @returns Promise with match details or null if not found
   */
  async function fetchCompleteMatchData(
    matchDetailMutation: any,
    matchId: string
  ): Promise<{
    bols: BolCandidate[]
    invoices: InvoiceCandidate[]
  } | null> {
    try {
      // Use the mutation's mutateAsync method
      const response = await matchDetailMutation.mutateAsync({
        params: {
          query: {
            match_id: matchId,
          },
        },
      })

      if (response && response.document_detail_rows) {
        const result = resolveMatchFromDetails(response)
        return result
      }

      return null
    } catch (error) {
      console.error('Error fetching match details:', error)
      return null
    }
  }

  /**
   * Convert match detail response to BOL/Invoice candidate format
   * @param matchDetails - match detail response from API
   * @returns object containing BOLs and invoices in candidate format
   */
  function resolveMatchFromDetails(matchDetails: MatchDetailResponse): {
    bols: BolCandidate[]
    invoices: InvoiceCandidate[]
  } {
    const result = {
      bols: [] as BolCandidate[],
      invoices: [] as InvoiceCandidate[],
    }

    // Group document detail rows by document ID to create candidate objects
    const documentGroups = new Map<string, any[]>()

    matchDetails.document_detail_rows?.forEach((row, index) => {
      const key = `${row.document_type}_${row.document_id}`

      if (!documentGroups.has(key)) {
        documentGroups.set(key, [])
      }
      documentGroups.get(key)!.push(row)
    })

    // Convert grouped rows back to candidate format
    documentGroups.forEach((rows, key) => {
      const [docType, docId] = key.split('_', 2)
      const firstRow = rows[0]

      if (docType === 'invoice') {
        // Create invoice candidate from document details
        const invoiceCandidate: InvoiceCandidate = {
          invoice_id: docId,
          invoice_number: firstRow.document_number,
          terminal: firstRow.terminal,
          details: rows.map((row) => ({
            type: 'supply', // Assuming supply type, could be enhanced
            product: row.product,
            total: row.product_total || 0,
            gross_volume: row.gross_volume || 0,
            net_volume: row.net_volume || 0,
            per_unit_cost: row.per_unit_cost || 0,
            product_total: row.product_total || 0,
            tax_total: row.tax_total || 0,
            store_number: row.store_number,
          })),
          invoice_total: rows.reduce((sum, row) => sum + (row.total || 0), 0),
          match_id: matchDetails.id,
          // Add required fields
          linked_bol_numbers: [],
          linked_bol_ids: [],
          bol_numbers: [],
          inMatch: false,
        }
        result.invoices.push(invoiceCandidate)
      } else if (docType === 'bol') {
        // Create BOL candidate from document details
        const bolCandidate: BolCandidate = {
          bol_id: docId,
          bol_number: firstRow.document_number,
          bol_date: firstRow.document_date,
          terminal_name: firstRow.terminal?.internal_name || firstRow.terminal?.source_name || 'Unknown',
          products: rows.map((row) => ({
            product_id: row.product?.id || '',
            product_name: row.product?.internal_name || row.product?.source_name || 'Unknown',
            gross_volume: row.gross_volume || 0,
            net_volume: row.net_volume || 0,
            product_total: row.product_total || 0,
            tax_total: row.tax_total || 0,
          })),
          bol_total: rows.reduce((sum, row) => sum + (row.total || 0), 0),
          match_id: matchDetails.id,
        }
        result.bols.push(bolCandidate)
      }
    })

    return result
  }

  // Create the mutation hook for match details
  const matchDetailMutation = useMatchDetailMutation()

  return {
    // Combined data
    data: combinedData,

    // Backward compatibility - returns data for specific search type
    getDataForSearchType,

    // New match details-based functionality
    checkForLinkedData,
    fetchCompleteMatchData: (matchId: string) => fetchCompleteMatchData(matchDetailMutation, matchId),
    resolveMatchFromDetails,

    // Individual query data
    bolData: combinedData.bols,
    invoiceData: combinedData.invoices,

    // Combined query states
    isLoading,
    error,
    refetch,
    isSuccess,
    isFetching,

    // Individual query states for debugging
    bolQuery: {
      isLoading: bolQuery.isLoading,
      error: bolQuery.error,
      isSuccess: bolQuery.isSuccess,
    },
    invoiceQuery: {
      isLoading: invoiceQuery.isLoading,
      error: invoiceQuery.error,
      isSuccess: invoiceQuery.isSuccess,
    },
  }
}
